import React, { useMemo } from 'react';
import {
  Building2, CheckCircle2, DoorOpen, ClipboardCheck, Clock, Banknote, Wrench, AlertTriangle,
} from 'lucide-react';
import { theme } from '../shared/theme';
import { useLanguage } from '../../components/LanguageContext';
import '../shared/portals.css';

// ============================================================
// AdminPmDashboard — the Property Management overview, in the app.
//
// The console opens on numbers and this tab opened on a flat list of 99
// properties, which is the same data with the answer left out. These are the
// questions the team opens the console to ask: how many are let, how many are
// sitting empty, what has been invoiced against what has landed, who is
// overdue, what is waiting to be fixed.
//
// EVERY FIGURE IS DERIVED FROM /pm/all, which the tab already fetches — no
// second call, no new endpoint, nothing to deploy. And every one is derived
// the same way the console derives it, from the same records, so the two
// cannot drift into disagreeing about how many properties there are.
//
// Where the data cannot answer, the tile says so rather than showing a
// confident zero. A zero and "we do not know" look identical on a dashboard
// and mean opposite things.
// ============================================================

interface Unit {
  id: string;
  name?: string;
  project?: string;
  unitNumber?: string;
  status?: string;
  completionDate?: string;
  estimatedCompletionDate?: string;
  currentTenant?: { name?: string; contractEnd?: string } | null;
  rentSchedule?: { chequeNo?: string; amount?: number; dueDate?: string; status?: string; paid?: boolean }[];
  maintenance?: { status?: string }[];
}

const AED = (n: number) => `AED ${Math.round(n || 0).toLocaleString('en-AE')}`;
const isPaid = (r: { paid?: boolean; status?: string }) => r.paid === true || (r.status || '').toLowerCase() === 'paid';

// ---- THE CONSOLE'S OWN RULES, COPIED EXACTLY -------------------------
//
// These three decide every headline figure, so they are transcribed from
// pm-admin.html rather than reinvented. The first attempt here used the
// DLD-derived `facts.construction.offplan`, which is a different question —
// it reported 76 off-plan and 19 vacant where the console reports 34 and 61,
// on the same 99 properties. Two dashboards disagreeing about how many flats
// are empty is worse than one dashboard fewer.
//
// Rented wins over everything; off-plan is decided PURELY by a completion
// date in the future (not delivered yet); vacant is what is left. Keep these
// in step with pm-admin.html if that ever changes.
export const isRented = (u: Unit) => u.status === 'Rented' || !!u.currentTenant;

export const isOffplan = (u: Unit) => {
  if (isRented(u)) return false;
  const c = u.completionDate || u.estimatedCompletionDate;
  if (!c || Number.isNaN(Date.parse(c))) return false;
  return new Date(c) > new Date();
};

export function summarise(units: Unit[]) {
  const today = new Date();
  const in90 = new Date(today.getTime() + 90 * 86400000);

  let annualRent = 0;
  let collected = 0;
  let overdue = 0;
  const defaulters = new Set<string>();
  let renewals = 0;
  const jobs = { open: 0, inProgress: 0, resolved: 0 };
  const byProperty: { label: string; amount: number }[] = [];

  units.forEach((u) => {
    const rent = u.rentSchedule || [];
    const unitRent = rent.reduce((n, r) => n + (Number(r.amount) || 0), 0);
    annualRent += unitRent;
    if (unitRent > 0) byProperty.push({ label: u.name || u.project || u.unitNumber || u.id, amount: unitRent });

    rent.forEach((r) => {
      const amt = Number(r.amount) || 0;
      if (isPaid(r)) { collected += amt; return; }
      // Unpaid AND past its date — an unpaid cheque due next month is not
      // overdue, and counting it as such would invent a debt.
      const due = r.dueDate ? new Date(r.dueDate) : null;
      if (due && !Number.isNaN(due.getTime()) && due < today) {
        overdue += amt;
        defaulters.add(u.id);
      }
    });

    const end = u.currentTenant?.contractEnd ? new Date(u.currentTenant.contractEnd) : null;
    if (end && !Number.isNaN(end.getTime()) && end >= today && end <= in90) renewals += 1;

    (u.maintenance || []).forEach((m) => {
      const s = (m.status || 'open').toLowerCase();
      if (s === 'resolved') jobs.resolved += 1;
      else if (s === 'in_progress' || s === 'in_review') jobs.inProgress += 1;
      else jobs.open += 1;
    });
  });

  // Exactly the console's partition: every property is one of the three.
  const rented = units.filter(isRented).length;
  const offplan = units.filter(isOffplan).length;
  const vacant = units.length - rented - offplan;

  byProperty.sort((a, b) => b.amount - a.amount);

  return {
    properties: units.length,
    rented,
    vacant,
    offplan,
    renewals,
    annualRent,
    collected,
    outstanding: Math.max(0, annualRent - collected),
    overdue,
    defaulters: defaulters.size,
    jobs,
    byProperty: byProperty.slice(0, 6),
    occupancy: units.length ? Math.round((rented / units.length) * 100) : 0,
  };
}

const AdminPmDashboard: React.FC<{ units: Unit[]; onSeeUnits: () => void }> = ({ units, onSeeUnits }) => {
  const { t } = useLanguage();
  const s = useMemo(() => summarise(units), [units]);

  return (
    <div className="pt-fade">
      {/* The count line first: everything under it is a slice of this. */}
      <div className="pm-dash-grid">
        <Tile icon={Building2} label={t('pdProperties')} value={s.properties} onClick={onSeeUnits} />
        <Tile icon={CheckCircle2} label={t('pdRented')} value={s.rented} tone={theme.success} />
        <Tile icon={DoorOpen} label={t('pdVacant')} value={s.vacant} tone={theme.warning} />
        <Tile icon={ClipboardCheck} label={t('pdOffplan')} value={s.offplan} tone={theme.info} />
        <Tile icon={Clock} label={t('pdRenewals')} value={s.renewals} tone={s.renewals ? theme.warning : undefined} />
        <Tile icon={Banknote} label={t('pdAnnualRent')} money={AED(s.annualRent)} />
      </div>

      {/* The two that cost money if ignored, given their own weight. */}
      {(s.overdue > 0 || s.defaulters > 0) && (
        <div className="pm-dash-alerts">
          <Alert tone="danger" value={AED(s.overdue)} label={t('pdOverdue')} sub={`${t('pdAcross')} ${s.defaulters} ${s.defaulters === 1 ? t('pdUnit') : t('pdUnits')}`} />
          <Alert tone="warn" value={String(s.defaulters)} label={t('pdDefaulters')} sub={t('pdDefaultersSub')} />
        </div>
      )}

      <div className="pt-card" style={{ padding: 15, marginBottom: 12 }}>
        <div className="pm-dash-h">{t('pdRentCollection')}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, marginTop: 10 }}>
          <Figure label={t('pdInvoiced')} value={AED(s.annualRent)} tone={theme.info} />
          <Figure label={t('pdCollected')} value={AED(s.collected)} tone={theme.success} />
          <Figure label={t('pdOutstanding')} value={AED(s.outstanding)} tone={theme.warning} />
        </div>
        <div className="pm-dash-bar" style={{ marginTop: 12 }}>
          <span style={{ width: `${s.annualRent ? Math.min(100, Math.round((s.collected / s.annualRent) * 100)) : 0}%` }} />
        </div>
        <div style={{ fontSize: 11.5, color: theme.slateLight, marginTop: 6, fontWeight: 600 }}>
          {AED(s.collected)} / {AED(s.annualRent)}
        </div>
      </div>

      <div className="pt-card" style={{ padding: 15, marginBottom: 12 }}>
        <div className="pm-dash-h">{t('pdServiceRequests')}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, marginTop: 10 }}>
          <Figure label={t('isStatusOpen')} value={String(s.jobs.open)} tone={theme.warning} icon={Wrench} />
          <Figure label={t('isStatusInProgress')} value={String(s.jobs.inProgress)} tone={theme.info} />
          <Figure label={t('isStatusResolved')} value={String(s.jobs.resolved)} tone={theme.success} />
        </div>
      </div>

      {s.byProperty.length > 0 && (
        <div className="pt-card" style={{ padding: 15, marginBottom: 12 }}>
          <div className="pm-dash-h">{t('pdIncomeByProperty')}</div>
          {s.byProperty.map((r) => (
            <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderTop: `1px solid ${theme.line}` }}>
              <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: theme.slate, overflowWrap: 'anywhere' }}>{r.label}</span>
              <span className="pm-dash-minibar">
                <span style={{ width: `${s.byProperty[0].amount ? Math.round((r.amount / s.byProperty[0].amount) * 100) : 0}%` }} />
              </span>
              <span style={{ fontSize: 13, fontWeight: 800, color: theme.ink, whiteSpace: 'nowrap' }}>{AED(r.amount)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Tile: React.FC<{ icon: React.ComponentType<any>; label: string; value?: number; money?: string; tone?: string; onClick?: () => void }> = ({ icon: Icon, label, value, money, tone, onClick }) => (
  <div className={`pt-card pm-dash-tile ${onClick ? 'pt-tap' : ''}`} onClick={onClick} role={onClick ? 'button' : undefined}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: theme.slate, fontSize: 11.5, fontWeight: 700 }}>
      <Icon size={13} color={tone || theme.gold} /> {label}
    </div>
    <div style={{ fontSize: money ? 17 : 24, fontWeight: 800, color: tone || theme.ink, marginTop: 4, lineHeight: 1.15 }}>
      {money || (typeof value === 'number' ? value.toLocaleString('en-GB') : '—')}
    </div>
  </div>
);

const Alert: React.FC<{ tone: 'danger' | 'warn'; value: string; label: string; sub: string }> = ({ tone, value, label, sub }) => (
  <div className={`pm-dash-alert is-${tone}`}>
    <div style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', opacity: 0.9 }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 800, marginTop: 3 }}>{value}</div>
    <div style={{ fontSize: 11.5, fontWeight: 600, opacity: 0.85, marginTop: 2 }}>{sub}</div>
  </div>
);

const Figure: React.FC<{ label: string; value: string; tone?: string; icon?: React.ComponentType<any> }> = ({ label, value, tone, icon: Icon }) => (
  <div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 700, color: theme.slate }}>
      {Icon && <Icon size={12} />} {label}
    </div>
    <div style={{ fontSize: 18, fontWeight: 800, color: tone || theme.ink, marginTop: 2 }}>{value}</div>
  </div>
);

export default AdminPmDashboard;
